import { httpResource } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  ErrorMeasuresDto,
  GeneralDto,
  HierarchyDto,
} from '@api-types/dtos/create-analysis-configuration.dto';
import { AnalysisCalculationLevel } from '@api-types/enums/analysis-calculation-level.enum';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { ForecastVersionResponse } from '@api-types/responses/forecast-version.response';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CyclesStore } from 'consensus/cycles/services/cycles.store';
import { AnalysisConfigurationSummaryComponent } from 'demand-forecasting/analysis/components/analysis-configuration-summary/analysis-configuration-summary.component';
import {
  AnalysisConfigurationSummary,
  formatSummaryMonths,
  SUMMARY_ANALYSIS_TYPE_KEYS,
  SUMMARY_CALCULATION_LEVEL_KEYS,
  SUMMARY_EMPTY_VALUE,
  SUMMARY_HIERARCHY_SEPARATOR,
  SUMMARY_LIST_SEPARATOR,
  SUMMARY_MISSING_VALUE_NOTE_KEYS,
  SummaryRow,
} from 'demand-forecasting/analysis/components/analysis-configuration-summary/analysis-configuration-summary.model';
import {
  createMeasureVisibility,
  getSelectedMeasureColumns,
} from 'demand-forecasting/analysis/services/measure-display';
import { selectConfigurationId } from 'demand-forecasting/store/router-state/router.selectors';

@Component({
  selector: 'pm-summary-step-analysis-configuration',
  templateUrl: './summary-step-analysis-configuration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AnalysisConfigurationSummaryComponent],
})
export class SummaryStepAnalysisConfigurationComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly cyclesStore = inject(CyclesStore);

  private readonly isMeasureVisible = createMeasureVisibility(this.store);
  private readonly configurationId = this.store.selectSignal(
    selectConfigurationId,
  );

  public readonly general = input.required<GeneralDto>();
  public readonly hierarchy = input.required<HierarchyDto>();
  public readonly errorMeasures = input.required<ErrorMeasuresDto>();
  public readonly consensusEvaluated = input(false);

  public readonly validChange = output<boolean>();

  private readonly forecastVersionsResource = httpResource<
    ForecastVersionResponse[]
  >(() => {
    const configurationId = this.configurationId();
    const general = this.general();
    if (configurationId === undefined || !general.forecastVersionId) {
      return undefined;
    }
    return `${environment.api.url}/results/of-configuration/${configurationId}`;
  });

  private readonly generalRows = computed((): SummaryRow[] => {
    const general = this.general();
    const rows: SummaryRow[] = [
      { label: 'ANALYSIS.DETAILS.NAME', value: general.name },
      {
        label: 'ANALYSIS.DETAILS.ANALYSIS_TYPE',
        value: this.translate.instant(
          SUMMARY_ANALYSIS_TYPE_KEYS[general.analysisType],
        ),
      },
    ];

    if (general.analysisType === AnalysisType.ROLLING) {
      rows.push(
        {
          label: 'ANALYSIS.CONTEXT_FREEZE_PERIOD',
          value: formatSummaryMonths(this.translate, general.freezePeriod),
        },
        {
          label: 'ANALYSIS.DETAILS.LOOKBACK_PERIOD',
          value: formatSummaryMonths(this.translate, general.lookbackPeriod),
        },
      );
      return rows;
    }

    const cycle = this.cyclesStore
      .cycles()
      .find((candidate) => candidate.id === general.consensusCycleId);
    if (cycle) {
      rows.push({
        label: 'ANALYSIS.CYCLE',
        value: this.translate.instant('ANALYSIS.CYCLE_VALUE', {
          name: cycle.name,
          date: this.dateUtilsService.formatDateBasedOnLanguage(
            cycle.endDate?.toString(),
          ),
        }),
      });
    }

    if (general.forecastVersionId) {
      const version = this.forecastVersionsResource
        .value()
        ?.find((candidate) => candidate.id === general.forecastVersionId);
      rows.push({
        label: 'ANALYSIS.FORECAST_VERSION',
        value:
          this.dateUtilsService.formatDateBasedOnLanguage(version?.createdAt) ||
          SUMMARY_EMPTY_VALUE,
      });
    }
    return rows;
  });

  private readonly timeseriesRows = computed((): SummaryRow[] => [
    {
      label: 'ANALYSIS.DETAILS.TIMESERIES_COMPARED',
      value: [
        this.translate.instant('ANALYSIS.FORECAST'),
        ...this.general().comparisonTimeseriesNames,
        ...(this.consensusEvaluated()
          ? [this.translate.instant('ANALYSIS.DETAILS.CONSENSUS_SERIES')]
          : []),
      ].join(SUMMARY_LIST_SEPARATOR),
    },
  ]);

  private readonly measureRows = computed((): SummaryRow[] => {
    const errorMeasures = this.errorMeasures();
    const measures = getSelectedMeasureColumns(
      errorMeasures,
      this.isMeasureVisible,
      this.translate.instant('ANALYSIS.ACCURACY_SUFFIX'),
    ).map((column) => column.header);
    const calculationLevel = this.consensusEvaluated()
      ? AnalysisCalculationLevel.HIGHEST
      : (errorMeasures.calculationLevel ?? AnalysisCalculationLevel.HIGHEST);

    return [
      {
        label: 'ANALYSIS.DETAILS.ERROR_MEASURES',
        value: measures.join(SUMMARY_LIST_SEPARATOR) || SUMMARY_EMPTY_VALUE,
      },
      {
        label: 'ANALYSIS.DETAILS.CALCULATION_LEVEL',
        value: this.translate.instant(
          SUMMARY_CALCULATION_LEVEL_KEYS[calculationLevel],
        ),
      },
    ];
  });

  protected readonly summary = computed((): AnalysisConfigurationSummary => {
    const general = this.general();
    return {
      general: this.generalRows(),
      timeseries: this.timeseriesRows(),
      missingValueNoteKey:
        general.analysisType === AnalysisType.ROLLING
          ? SUMMARY_MISSING_VALUE_NOTE_KEYS[general.missingValueStrategy]
          : undefined,
      hierarchy: [
        {
          label: 'ANALYSIS.DETAILS.LEVELS',
          value:
            this.hierarchy().hierarchyOrder.join(SUMMARY_HIERARCHY_SEPARATOR) ||
            SUMMARY_EMPTY_VALUE,
        },
      ],
      measures: this.measureRows(),
      consensusEvaluated: this.consensusEvaluated(),
    };
  });

  ngOnInit(): void {
    this.validChange.emit(true);
  }
}
